import { cn } from '../../lib/cn'
import s from './ProgressBar.module.css'

/**
 * Barre d'avancement — report de `.team-prog` du prototype validé (`mocks/ghp-bo/app.css`), où
 * elle porte l'avancement d'une équipe sur sa tournée.
 *
 * **Pas de primitive**, et c'est le corollaire de la règle 3 : il n'y a aucun comportement à
 * emprunter. Une barre de progression ne reçoit pas le focus, ne s'actionne pas, n'a pas de
 * clavier — la plateforme fournit déjà tout ce qui compte par `role="progressbar"` et ses trois
 * attributs de valeur. `<progress>` natif aurait été le réflexe, mais il traîne une apparence
 * propre à chaque navigateur qu'il faut ensuite neutraliser par des pseudo-éléments préfixés :
 * plus de CSS pour retirer un style que pour en poser un.
 *
 * ⚠️ **Le texte n'est pas dans la barre.** Le prototype l'écrit à côté (`.team-tile-prog`,
 * « 68% avancement »), et c'est ce que `label` accompagne, pas ce qu'il remplace : la barre reste
 * muette à l'œil, `aria-label` et `aria-valuetext` la nomment pour qui ne la voit pas.
 */

export interface ProgressBarProps {
  /**
   * Avancement, entre 0 et 1.
   *
   * ⚠️ **Hors bornes, on ramène ; on ne lève pas** (M21). Un `1.4` venu d'un compteur fautif doit
   * rendre une barre pleine, pas démonter l'écran qui la contient — et un `NaN`, qui traverse
   * volontiers une division, rendrait `inline-size: NaN%` sans que rien ne le signale.
   */
  value: number
  /** Nom accessible : la barre n'a pas de libellé visible qui lui soit rattaché. */
  label: string
  className?: string
}

export function ProgressBar({ value, label, className }: ProgressBarProps) {
  const ratio = clampRatio(value)
  const percent = Math.round(ratio * 100)

  return (
    <div
      className={cn(s.root, className)}
      role="progressbar"
      aria-label={label}
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      // ⚠️ Sans `aria-valuetext`, un lecteur d'écran annonce « 68 » sans unité. Le pourcentage est
      // la seule lecture juste : la barre ne connaît ni le total ni ce qu'il compte.
      aria-valuetext={`${String(percent)} %`}
    >
      <span
        className={s.fill}
        style={{ inlineSize: `${String(percent)}%` }}
        data-empty={percent === 0 ? '' : undefined}
      />
    </div>
  )
}

/**
 * Ramène dans `[0, 1]`, `NaN` compris.
 *
 * `Number.isFinite` plutôt qu'un test d'égalité à `NaN` : il attrape aussi `Infinity`, que
 * `Math.min` laisserait passer à 1 mais qui signale la même donnée fautive.
 */
function clampRatio(value: number): number {
  if (!Number.isFinite(value)) return 0

  return Math.min(1, Math.max(0, value))
}
